import { PaymentEntryType } from '@shared-enums';

import {
  APPLIED_PAYMENT_STATUS,
  INVOICE_BALANCE_EPSILON,
} from './card-state.utils';
import { round2 } from './math.utils';

/** `status >= APPLIED_PAYMENT_STATUS` — i.e. approved and transferred. */
export const isPaymentApproved = (status: number): boolean =>
  status >= APPLIED_PAYMENT_STATUS;

/**
 * The id of the invoice/purchase's active (approved) Cancellation payment,
 * if any. Shared by the sales and purchase payment modals' `canDeletePayment`
 * rule — see that function's doc comment for the business rule itself.
 * Callers map their own domain payments (`IInvoicePayments`/
 * `IPurchasePayments`, whose id field is named differently on each) to this
 * generic `{ id, status, type }` shape.
 */
export const findActiveCancellationId = (
  payments: readonly {
    id: number;
    status: number;
    type: string;
  }[],
): number | null => {
  const cancellation = payments.find(
    (payment) =>
      payment.type === PaymentEntryType.CANCELLATION &&
      payment.status === APPLIED_PAYMENT_STATUS,
  );
  return cancellation ? cancellation.id : null;
};

/**
 * Payments can be deleted in any order. The one exception: while an approved
 * Cancellation payment exists on the invoice/purchase, only that cancellation
 * can be deleted — every other payment (earlier or later) stays locked until
 * it is removed, since deleting it is what re-opens the invoice for further
 * payment changes. Confirmed with business (see payment-modal's canDeletePayment).
 */
export const canDeletePayment = (
  paymentId: number,
  activeCancellationId: number | null,
): boolean =>
  activeCancellationId === null || paymentId === activeCancellationId;

/** How much of the total is still pending after applied payments, floored at 0. */
export const appliedPendingBalance = (
  total: number,
  appliedPaid: number,
): number => Math.max(0, round2(total - appliedPaid));

export const hasAssociatedPayments = (appliedPaid: number): boolean =>
  appliedPaid > INVOICE_BALANCE_EPSILON;

/**
 * Same rule as the Cartera / CxP card: a real (applied) payment exists and
 * nothing is left to pay, even if credit notes / refunds took the total below 0.
 */
export const isFullyPaid = (total: number, appliedPaid: number): boolean =>
  hasAssociatedPayments(appliedPaid) &&
  total - appliedPaid <= INVOICE_BALANCE_EPSILON;

/** Oldest-to-newest by `date`. Shared sort used by both payment modals'
 * "payments history" list — kept generic so each modal can still layer its
 * own extra per-row computation (e.g. `isFullPayment`) on top. */
export const sortPaymentsByDate = <T extends { date: string }>(
  payments: readonly T[],
): T[] =>
  [...payments].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );

export const advancePaymentTotal = (
  advancePayments: readonly { value: number }[],
): number =>
  advancePayments.reduce((total, payment) => total + payment.value, 0);

export const hasAdvancePayment = (
  advancePayments: readonly unknown[],
): boolean => advancePayments.length > 0;

export const hasMultipleAdvancePayments = (
  advancePayments: readonly unknown[],
): boolean => advancePayments.length > 1;

/**
 * Releases every treasury (advance payment) linked to a deleted payment,
 * logging rather than throwing on a per-treasury failure — one bad release
 * shouldn't stop the others from going through. `releaseOne` is the actual
 * backend call (e.g. `paymentsService.relateAdvancePayment('Remove', ...)`),
 * left to the caller since it differs by one enum member and one id field
 * between the sales and purchase payment modals.
 */
export const releaseRelatedTreasury = async (
  treasuries: readonly { id_treasury: number }[] | null | undefined,
  releaseOne: (treasury: { id_treasury: number }) => Promise<unknown>,
): Promise<void> => {
  await Promise.all(
    (treasuries ?? []).map((treasury) =>
      releaseOne(treasury).catch((error) => console.error(error)),
    ),
  );
};
